import {
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { GreenText, LabelField } from 'components'
import * as controllers from 'controllers'
import { CompanyAddressDto, CompanyDto, MichiganTitleVehicleDto, PersonDto } from 'dtos'
import { useFormikContext } from 'formik'
import { Junction } from 'localConstants'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { formatAddress, getComparableAddress } from 'utils'

interface SelectedVehicleMichiganTitleApplicationPacketCardProps {
  close: () => void
}

export default function SelectedVehicleMichiganTitleApplicationPacketCard({
  close
}: SelectedVehicleMichiganTitleApplicationPacketCardProps) {
  const { errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values } =
    useFormikContext<MichiganTitleVehicleDto>()

  const [isGettingDocumentSignerPeople, setIsGettingDocumentSignerPeople] = useState<boolean>(false)
  const [documentSignerPeople, setDocumentSignerPeople] = useState<PersonDto[]>([])
  const [isGettingLienHolderCompanies, setIsGettingLienHolderCompanies] = useState<boolean>(false)
  const [isGettingLienHolderCompanyAddresses, setIsGettingLienHolderCompanyAddresses] = useState<boolean>(false)
  const [lienHolderCompanies, setLienHolderCompanies] = useState<CompanyDto[]>([])
  const [lienHolderCompanyAddresses, setLienHolderCompanyAddresses] = useState<CompanyAddressDto[]>([])

  useEffect(() => {
    setIsGettingDocumentSignerPeople(true)
    controllers
      .getPeople({
        page: 0,
        pageSize: 100,
        order: 'asc',
        orderBy: 'name',
        permission: 'TITLES_ADD_EDIT',
        statuses: ['isActive']
      })
      .then(res => {
        setDocumentSignerPeople(res.value)
      })
      .finally(() => {
        setIsGettingDocumentSignerPeople(false)
      })

    setIsGettingLienHolderCompanies(true)
    controllers
      .getCompanies({
        page: 0,
        pageSize: 100,
        order: 'asc',
        orderBy: 'name',
        statuses: ['isActive', 'isLienHolderCompany'],
        junction: Junction.AND
      })
      .then(res => {
        setLienHolderCompanies(res.value)
      })
      .finally(() => {
        setIsGettingLienHolderCompanies(false)
      })
  }, [])

  useEffect(() => {
    if (values.michiganTitleLienHolderCompany?.companyGuid) {
      setIsGettingLienHolderCompanyAddresses(true)
      controllers
        .getCompanyAddresses(values.michiganTitleLienHolderCompany?.companyGuid, {
          page: 0,
          pageSize: 100,
          statuses: ['isActive']
        })
        .then(res => {
          setLienHolderCompanyAddresses(res.value)
        })
        .finally(() => {
          setIsGettingLienHolderCompanyAddresses(false)
        })
    }
  }, [values.michiganTitleLienHolderCompany?.companyGuid])

  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='flex-start'>
          <Grid item xs={12}>
            <Typography fontWeight='bold' variant='h6'>
              Selected Vehicle - Michigan Title Application Packet
            </Typography>
          </Grid>

          <Grid item xs={12} container spacing={5} alignItems='flex-start'>
            <Grid item xs={12} sm={4} container spacing={1} alignItems='center'>
              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleOwnerName && errors.michiganTitleOwnerName)}
                  helperText={touched.michiganTitleOwnerName && errors.michiganTitleOwnerName}
                  label='Owner Name'
                  name='michiganTitleOwnerName'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleOwnerName || ''}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleOwnerAddress1 && errors.michiganTitleOwnerAddress1)}
                  helperText={touched.michiganTitleOwnerAddress1 && errors.michiganTitleOwnerAddress1}
                  label='Owner Address'
                  name='michiganTitleOwnerAddress1'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleOwnerAddress1 || ''}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleOwnerCity && errors.michiganTitleOwnerCity)}
                  helperText={touched.michiganTitleOwnerCity && errors.michiganTitleOwnerCity}
                  label='Owner City'
                  name='michiganTitleOwnerCity'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleOwnerCity || ''}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleOwnerCounty && errors.michiganTitleOwnerCounty)}
                  helperText={touched.michiganTitleOwnerCounty && errors.michiganTitleOwnerCounty}
                  label='Owner County'
                  name='michiganTitleOwnerCounty'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleOwnerCounty || ''}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleOwnerCountry && errors.michiganTitleOwnerCountry)}
                  helperText={touched.michiganTitleOwnerCountry && errors.michiganTitleOwnerCountry}
                  label='Owner Country'
                  name='michiganTitleOwnerCountry'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleOwnerCountry || ''}
                />
              </Grid>

              <Grid item xs={12}>
                <LabelField label='Owner State' value='MI' />
                {/* <TextField                  
                  error={Boolean(touched.michiganTitleOwnerState && errors.michiganTitleOwnerState)}
                  helperText={touched.michiganTitleOwnerState && errors.michiganTitleOwnerState}
                  label='Owner State'
                  name='michiganTitleOwnerState'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleOwnerState || ''}
                /> */}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleOwnerPostalCode && errors.michiganTitleOwnerPostalCode)}
                  helperText={touched.michiganTitleOwnerPostalCode && errors.michiganTitleOwnerPostalCode}
                  label='Owner Postal Code'
                  name='michiganTitleOwnerPostalCode'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleOwnerPostalCode || ''}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleOwnerPhone && errors.michiganTitleOwnerPhone)}
                  helperText={touched.michiganTitleOwnerPhone && errors.michiganTitleOwnerPhone}
                  label='Owner Phone'
                  name='michiganTitleOwnerPhone'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleOwnerPhone || ''}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleOwnerIrsNumber && errors.michiganTitleOwnerIrsNumber)}
                  helperText={touched.michiganTitleOwnerIrsNumber && errors.michiganTitleOwnerIrsNumber}
                  label='Owner IRS #'
                  name='michiganTitleOwnerIrsNumber'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleOwnerIrsNumber || ''}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleOwnerDealerNumber && errors.michiganTitleOwnerDealerNumber)}
                  helperText={touched.michiganTitleOwnerDealerNumber && errors.michiganTitleOwnerDealerNumber}
                  label='Owner Dealer #'
                  name='michiganTitleOwnerDealerNumber'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleOwnerDealerNumber || ''}
                />
              </Grid>
            </Grid>

            <Grid item xs={12} sm={4} container spacing={1} alignItems='center'>
              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleSpecialMailerName && errors.michiganTitleSpecialMailerName)}
                  helperText={touched.michiganTitleSpecialMailerName && errors.michiganTitleSpecialMailerName}
                  label='Special Mailer Name'
                  name='michiganTitleSpecialMailerName'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleSpecialMailerName || ''}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleSpecialMailerAddress1 && errors.michiganTitleSpecialMailerAddress1)}
                  helperText={touched.michiganTitleSpecialMailerAddress1 && errors.michiganTitleSpecialMailerAddress1}
                  label='Special Mailer Address'
                  name='michiganTitleSpecialMailerAddress1'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleSpecialMailerAddress1 || ''}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleSpecialMailerCity && errors.michiganTitleSpecialMailerCity)}
                  helperText={touched.michiganTitleSpecialMailerCity && errors.michiganTitleSpecialMailerCity}
                  label='Special Mailer City'
                  name='michiganTitleSpecialMailerCity'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleSpecialMailerCity || ''}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleSpecialMailerState && errors.michiganTitleSpecialMailerState)}
                  helperText={touched.michiganTitleSpecialMailerState && errors.michiganTitleSpecialMailerState}
                  label='Special Mailer State'
                  name='michiganTitleSpecialMailerState'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleSpecialMailerState || ''}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleSpecialMailerPostalCode && errors.michiganTitleSpecialMailerPostalCode)}
                  helperText={touched.michiganTitleSpecialMailerPostalCode && errors.michiganTitleSpecialMailerPostalCode}
                  label='Special Mailer Postal Code'
                  name='michiganTitleSpecialMailerPostalCode'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.michiganTitleSpecialMailerPostalCode || ''}
                />
              </Grid>
            </Grid>

            <Grid item xs={12} sm={4} container spacing={1} alignItems='center'>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.michiganTitleRegisteredImporterWillNotReceiveTitle}
                      onChange={e => {
                        setFieldValue(
                          'michiganTitleRegisteredImporterWillNotReceiveTitle',
                          values.michiganTitleRegisteredImporterWillNotReceiveTitle === undefined
                            ? true
                            : !values.michiganTitleRegisteredImporterWillNotReceiveTitle
                        )
                      }}
                    />
                  }
                  label='Registered Importer Will Not Receive Title'
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.michiganTitleBaseMsrp && errors.michiganTitleBaseMsrp)}
                  helperText={touched.michiganTitleBaseMsrp && errors.michiganTitleBaseMsrp}
                  InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                  label='Base MSRP'
                  name='michiganTitleBaseMsrp'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type='number'
                  value={values.michiganTitleBaseMsrp || ''}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  disabled={isGettingLienHolderCompanies}
                  fullWidth
                  InputProps={{
                    endAdornment: isGettingLienHolderCompanies ? (
                      <InputAdornment position='end' sx={{ mr: 2.4 }}>
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ) : (
                      <></>
                    )
                  }}
                  label='Lien Holder Company'
                  onChange={e => {
                    const michiganTitleLienHolderCompany = JSON.parse(e.target.value)
                    setFieldValue('michiganTitleLienHolderCompanyAddress', undefined)
                    setFieldValue('michiganTitleLienHolderCompany', michiganTitleLienHolderCompany)
                  }}
                  select
                  size='small'
                  value={
                    values.michiganTitleLienHolderCompany
                      ? JSON.stringify({
                          companyGuid: values.michiganTitleLienHolderCompany.companyGuid,
                          name: values.michiganTitleLienHolderCompany.name
                        })
                      : undefined
                  }
                >
                  {lienHolderCompanies
                    .map(lienHolderCompany => ({ companyGuid: lienHolderCompany.companyGuid, name: lienHolderCompany.name }))
                    .map(lienHolderCompany => (
                      <MenuItem key={lienHolderCompany.companyGuid} value={JSON.stringify(lienHolderCompany)}>
                        {lienHolderCompany.name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  disabled={isSubmitting || isGettingLienHolderCompanyAddresses || !values.michiganTitleLienHolderCompany}
                  fullWidth
                  InputProps={{
                    endAdornment: isGettingLienHolderCompanyAddresses ? (
                      <InputAdornment position='end' sx={{ mr: 2.4 }}>
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ) : (
                      <></>
                    )
                  }}
                  label='Lien Holder Company Address'
                  onChange={e => {
                    setFieldValue('michiganTitleLienHolderCompanyAddress', JSON.parse(e.target.value))
                  }}
                  select
                  size='small'
                  value={
                    values.michiganTitleLienHolderCompanyAddress
                      ? JSON.stringify(getComparableAddress(values.michiganTitleLienHolderCompanyAddress))
                      : undefined
                  }
                >
                  {lienHolderCompanyAddresses.map(lienHolderCompanyAddress => (
                    <MenuItem
                      key={lienHolderCompanyAddress.addressGuid}
                      value={JSON.stringify(getComparableAddress(lienHolderCompanyAddress))}
                    >
                      {formatAddress(lienHolderCompanyAddress, false)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <DatePicker
                  disabled={isSubmitting}
                  label='Vehicle Purchase Agreement Effective Date'
                  onChange={newValue => {
                    if (newValue) {
                      setFieldValue('michiganTitleVehiclePurchaseAgreementEffectiveDate', newValue.format('yyyy-MM-DD'))
                    } else {
                      setFieldValue('michiganTitleVehiclePurchaseAgreementEffectiveDate', undefined)
                    }
                  }}
                  slotProps={{
                    actionBar: {
                      actions: ['clear']
                    },
                    textField: {
                      error: Boolean(
                        touched.michiganTitleVehiclePurchaseAgreementEffectiveDate &&
                          errors.michiganTitleVehiclePurchaseAgreementEffectiveDate
                      ),
                      helperText:
                        touched.michiganTitleVehiclePurchaseAgreementEffectiveDate &&
                        errors.michiganTitleVehiclePurchaseAgreementEffectiveDate
                    }
                  }}
                  value={
                    values.michiganTitleVehiclePurchaseAgreementEffectiveDate
                      ? moment(values.michiganTitleVehiclePurchaseAgreementEffectiveDate)
                      : null
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  disabled={isGettingDocumentSignerPeople}
                  fullWidth
                  InputProps={{
                    endAdornment: isGettingDocumentSignerPeople ? (
                      <InputAdornment position='end' sx={{ mr: 2.4 }}>
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ) : (
                      <></>
                    )
                  }}
                  label='Document Signer Person'
                  select
                  size='small'
                  onChange={e => {
                    if (e.target.value) {
                      setFieldValue('michiganTitleDocumentSignerPerson', JSON.parse(e.target.value))
                    }
                  }}
                  value={
                    values.michiganTitleDocumentSignerPerson
                      ? JSON.stringify({
                          personGuid: values.michiganTitleDocumentSignerPerson.personGuid,
                          firstName: values.michiganTitleDocumentSignerPerson.firstName,
                          lastName: values.michiganTitleDocumentSignerPerson.lastName
                        })
                      : undefined
                  }
                >
                  {documentSignerPeople
                    .map(({ personGuid, firstName, lastName }) => ({ personGuid, firstName, lastName }))
                    .map(documentSignerPerson => (
                      <MenuItem key={documentSignerPerson.personGuid} value={JSON.stringify(documentSignerPerson)}>
                        {documentSignerPerson.firstName} {documentSignerPerson.lastName}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center' justifyContent='flex-end'>
              <Grid item>
                <Button
                  color='primary'
                  size='small'
                  onClick={() => {
                    handleSubmit()
                    close()
                  }}
                  variant='contained'
                >
                  SAVE AND CLOSE
                </Button>
              </Grid>

              <Grid item>
                <Button color='primary' size='small' type='submit' variant='contained'>
                  SAVE
                </Button>
              </Grid>

              <Grid item>
                <Button
                  color='primary'
                  disabled={
                    isSubmitting ||
                    !Boolean(values.michiganTitleOwnerName) ||
                    !Boolean(values.michiganTitleOwnerAddress1) ||
                    !Boolean(values.michiganTitleOwnerCity) ||
                    !Boolean(values.michiganTitleOwnerCounty) ||
                    !Boolean(values.michiganTitleOwnerCountry) ||
                    // !Boolean(values.michiganTitleOwnerState) || // Owner State will always be MI
                    !Boolean(values.michiganTitleOwnerPostalCode) ||
                    !Boolean(values.michiganTitleOwnerPhone) ||
                    !Boolean(values.michiganTitleSpecialMailerName) ||
                    !Boolean(values.michiganTitleSpecialMailerAddress1) ||
                    !Boolean(values.michiganTitleSpecialMailerCity) ||
                    !Boolean(values.michiganTitleSpecialMailerState) ||
                    !Boolean(values.michiganTitleSpecialMailerPostalCode) ||
                    !Boolean(values.michiganTitleBaseMsrp) ||
                    !Boolean(values.michiganTitleVehiclePurchaseAgreementEffectiveDate) ||
                    !Boolean(values.michiganTitleDocumentSignerPerson)
                  }
                  onClick={() => {
                    if (
                      window.confirm(
                        'Are you sure you wish to mark this application packet Ready for Police Book? This cannot be undone.'
                      )
                    ) {
                      setFieldValue('michiganTitleIsReadyForPoliceBook', true)
                      handleSubmit()
                    }
                  }}
                  size='small'
                  variant='contained'
                >
                  SAVE AND MARK READY FOR POLICE BOOK
                </Button>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Owner Name Field:**

Displays the name of the Owner, which will appear on the new vehicle title after the Title Application Packet is sent to the Michigan Secretary of State.

If a user selects a company as Owner or Special Mailer, the company name will be entered into the Owner or Special Mailer Name field, respectively. The selected company's Address 1 and Address 2 data from the “Companies” feature will populate the Owner Address or Special Mailer Address field (e.g., if Address 1 = "123 Main 
St" and Address 2 = "Suite A", the Owner Address field will be populated as "123 Main St Suite A").

If a user selects the Registered Importer as the Owner, the Registered Importer’s Importer # from the Registered Importer card under Company Details will appear in the Owner Dealer # field, and the Registered Importer IRS # will appear in the Owner IRS # field.

**Owner Address Field:**

Displays the Address 1 and Address 2 values of the selected Owner, merged into a single field (e.g., “123 Main St Suite A”).

**Owner City Field:**

Displays the city associated with the Owner's address.

**Owner Country Field:**

Displays the country associated with the Owner's address.

The Owner State field will always contain “MI” and cannot be edited by users. This enforces that Michigan Title applicants must have a Michigan address.

**Owner Postal Code Field:**

Displays the postal code associated with the Owner's address.

**Owner IRS# Field:**

Displays the IRS # of the selected Owner.

If the Exporter Company is selected as the Owner, the Exporter’s IRS # (from the Exporter Information card in Company Details) is displayed.

If the Registered Importer is selected as the Owner, the Registered Importer’s IRS # is displayed.

**Owner Dealer Number Field:**

Displays the Dealer Number of the selected Owner.

If the Exporter Company is selected as the Owner, the Exporter’s Dealer # (from the Exporter Information card in Company Details) is displayed.

If the Registered Importer is selected as the Owner, the Registered Importer’s Importer # is displayed.

**Special Mailer Name:**

The Special Mailer is the name and address that will be applied to the TR-114 - Special Mailing of Certificate Title document, which instructs the Michigan Secretary of State where the new vehicle title should be sent. The Special Mailer fields are empty by default.

**Special Mailer Address:**

The Special Mailer is the name and address that will be applied to the TR-114 - Special Mailing of Certificate Title document, which instructs the Michigan Secretary of State where the new vehicle title should be sent. The Special Mailer fields are empty by default.

**Special Mailer City Field:**

Displays the city associated with the Special Mailer.

**Special Mailer State Field:**

Displays the city associated with the Special Mailer.

**Special Mailer Postal Code Field:**

Displays the city associated with the Special Mailer.

**Registered Importer Will Not Receive Title Check Box:**

A checkbox that indicates whether the Registered Importer will not receive the vehicle title.

**Base MSRP:**

Free text field to enter the base MSRP (Manufacturer's Suggested Retail Price).

**Lien Holder Company Dropdown:**

Lists all companies marked as “Lien Holder Company” in Company Details.

**Vehicle Purchase Agreement Effective Date Picker:**

Allows users to select the effective date of the Vehicle Purchase Agreement.

The effective date is used to confirm the validity period of the agreement.

**Document Signer Dropdown:**

Contains the names of all users with VTS2 Access “Titles Add/Edit”

**SAVE AND CLOSE Button:**

Saves all entered information from the “Selected Vehicle – Michigan Title Application Packet” section into their respective fields on the Vehicle Details – Titles (tab) of the Michigan Title Application Packet card.

After saving, the vehicle is deselected and returned to the list of vehicles at the top of the Michigan Titles screen.

If a user later selects a vehicle from this list that already has saved Michigan Title Application Packet information, the information will be pre-populated into the input fields of the “Selected Vehicle – Michigan Title Application Packet” section.

**SAVE Button:**

Saves all entered information from the “Selected Vehicle – Michigan Title Application Packet” section into their respective fields on the Vehicle Details – Titles (tab) of the Michigan Title Application Packet card.

Does not deselect the vehicle or return it to the list of vehicles, allowing the user to continue editing the selected vehicle's information.

**SAVE AND MARK READY FOR POLICE BOOK Button:**

All fields in the “Selected Vehicle – Michigan Title Application Packet” section other than “Owner IRS#”, “Owner Dealer #”, “Lien Holder Company”, and “Lien Holder Company Address” must be populated for the “Save and Mark Application Packet Ready for Police Book” button to be enabled.

Pressing this button will show a pop-up asking, “Are you sure you wish to mark this application packet Ready for Police Book? This cannot be undone.”

If the user presses Yes, the selected vehicle will be moved down the screen into the “Police Book” table.`}
            </GreenText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
